import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { useModalStore } from "../store/report/modalStore";
import { useReportViewStore } from "../store/report/useReportViewStore";
import { useDashboardQuery } from "../hooks/reports/queries/useDashboardQueries";
import { useDashboardMutations } from "../hooks/reports/mutations/useDashboardMutations";
import DashboardPinCard from "../components/reports/dashboard/DashboardPinCard";
import DashboardHeader from "../components/reports/dashboard/DashboardHeader";
import GlobalFiltersPanel from "../components/reports/dashboard/GlobalFiltersPanel";
import "./ReportDashboard.css";
import { DashboardPin } from "../models/pageModels/ReportDashboard";
import { PinCardSkeleton, DashboardEmptyState, DashboardNotFoundState } from "../components/reports/dashboard/DashboardUIStates";
import { useRefreshLock } from "../hooks/reports/useRefreshLock";
import WarningMessages from "../components/UI/WarnginMessages";
import { OrganizationContext } from "../App";
import { FREE_PLAN_TYPE_ID } from "../constants/planTypes";

const getRefreshAvailabilityWait = (availableAt: Date) => {
  const diffMs = availableAt.getTime() - Date.now();
  if (diffMs <= 0) return "now";

  const totalMinutes = Math.ceil(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);

  return parts.join(" ");
};

const getRefreshLimitMessage = (refreshLimit?: {
  reached: boolean;
  maxRefreshesPerDay?: number;
  refreshesRemainingInPeriod?: number | null;
  nextRefreshAvailableAt: string | null;
}) => {
  if (!refreshLimit?.reached) return null;

  const maxRefreshes = refreshLimit.maxRefreshesPerDay ?? 1;
  const limitReachedText = `Your organization has used all ${maxRefreshes} dashboard refreshes available in the last 24 hours`;

  if (!refreshLimit.nextRefreshAvailableAt) {
    return `${limitReachedText}.`;
  }

  const nextRefreshAvailableAt = new Date(refreshLimit.nextRefreshAvailableAt);
  const availableAt = nextRefreshAvailableAt.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  });
  const waitTime = getRefreshAvailabilityWait(nextRefreshAvailableAt);

  return `${limitReachedText}. It can be refreshed again in ${waitTime}, after ${availableAt}.`;
};

const getRefreshCooldownMessage = (refreshCooldown?: {
  cooldownMinutes: number;
  reached: boolean;
  nextRefreshAvailableAt: string | null;
}) => {
  if (!refreshCooldown?.reached || !refreshCooldown.nextRefreshAvailableAt) return null;

  const availableAt = new Date(refreshCooldown.nextRefreshAvailableAt);
  return `This dashboard can only be refreshed once every ${refreshCooldown.cooldownMinutes} minutes. Try again in ${getRefreshAvailabilityWait(availableAt)}.`;
};

const ReportDashboardPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const targetGroupId = groupId ?? "";
  const [localPins, setLocalPins] = useState<DashboardPin[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [, setRestrictionClock] = useState(Date.now());
  const organizationContext = useContext(OrganizationContext);

  const openModal = useModalStore((s) => s.openModal);
  const setActiveGroupId = useReportViewStore((s) => s.setActiveGroupId);

  const { data, isLoading, isError } = useDashboardQuery(targetGroupId);
  const { pinMutation } = useDashboardMutations(targetGroupId);

  const { isLocked: isRefreshing } = useRefreshLock({ groupId: targetGroupId });

  const moveLocalPin = (sourceIndex: number, destinationIndex: number) => {
    if (!localPins || sourceIndex === destinationIndex) return;

    const boundedDestination = Math.max(0, Math.min(destinationIndex, localPins.length - 1));
    if (sourceIndex === boundedDestination) return;

    const items = Array.from(localPins);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(boundedDestination, 0, reorderedItem);
    setLocalPins(items);
  };

  useEffect(() => {
    setActiveGroupId(groupId ?? "");
    return () => setActiveGroupId("");
  }, [groupId, setActiveGroupId]);

  useEffect(() => {
    if (isRefreshing && isReorderMode) {
      setLocalPins(null);
      setIsReorderMode(false);
    }
  }, [isRefreshing, isReorderMode]);

  useEffect(() => {
    const interval = window.setInterval(() => setRestrictionClock(Date.now()), 30000);
    return () => window.clearInterval(interval);
  }, []);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !localPins) return;
    moveLocalPin(result.source.index, result.destination.index);
  };

  const handleConfirmReorder = async () => {
    if (!localPins) return;
    setIsSaving(true);
    try {
      await pinMutation.mutateAsync({
        action: "REORDER_DASHBOARD_PINS",
        pins: localPins.map((p, i) => ({ pinId: p.pinId, gridPosition: i }))
      });
      setLocalPins(null);
      setIsReorderMode(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelReorder = () => {
    setLocalPins(null);
    setIsReorderMode(false);
  };

  if (isLoading && !data) {
    return (
      <div className="report-dashboard">
        <DashboardHeader 
          groupId={targetGroupId}
          groupName="Loading..."
          pins={[]}
          isRefreshing={false}
          isLoading={true}
          isReorderMode={false}
          isSaving={false}
          onEnterReorderMode={() => {}}
          onCancelReorder={() => {}}
          onConfirmReorder={() => {}}
          refreshStatus={null}
          lastRefreshedAt={null}
          refreshLimitReached={false}
          refreshLimitMessage={null}
        />
        <div className="row mt-4">
          {[1, 2, 3].map((i) => <PinCardSkeleton key={`skeleton-${i}`} />)}
        </div>
      </div>
    );
  }
  
  if (isError || !data) {
    return <DashboardNotFoundState />;
  }

  const hasPins = data.pins.length > 0;
  const pinsToRender = isReorderMode && localPins ? localPins : data.pins;
  const refreshLimitReached = !!data.refreshLimit?.reached;
  const refreshLimitActive = refreshLimitReached && (!data.refreshLimit?.nextRefreshAvailableAt
    || new Date(data.refreshLimit.nextRefreshAvailableAt).getTime() > Date.now());
  const refreshCooldownActive = !!data.refreshCooldown?.reached
    && !!data.refreshCooldown.nextRefreshAvailableAt
    && new Date(data.refreshCooldown.nextRefreshAvailableAt).getTime() > Date.now();
  const refreshUnavailable = refreshLimitActive || refreshCooldownActive;
  const refreshUnavailableMessage = refreshLimitActive
    ? getRefreshLimitMessage(data.refreshLimit)
    : getRefreshCooldownMessage(data.refreshCooldown);
  const refreshUsageWarning = data.refreshLimit?.warningReached
    ? organizationContext.value?.planTypeId === FREE_PLAN_TYPE_ID
      ? `Your organization has used ${data.refreshLimit.refreshesUsedInPeriod} of ${data.refreshLimit.maxRefreshesPerDay} dashboard refreshes in the last 24 hours. Upgrade to the Standard Plan to get up to 100 dashboard refreshes per 24-hour period.`
      : `Your organization has used ${data.refreshLimit.refreshesUsedInPeriod} of ${data.refreshLimit.maxRefreshesPerDay} dashboard refreshes in the last 24 hours.`
    : null;

 return (
    <div className="report-dashboard">
      {refreshUsageWarning && (
        <WarningMessages
          messages={[refreshUsageWarning]}
          messageClassName="prominent"
          showIcon={true}
        />
      )}
      <DashboardHeader 
        groupId={targetGroupId}
        groupName={data.groupName}
        pins={data.pins}
        isRefreshing={isRefreshing}
        isLoading={false}
        isReorderMode={isReorderMode}
        isSaving={isSaving}
        onEnterReorderMode={() => {
          setLocalPins([...data.pins]);
          setIsReorderMode(true);
        }}
        onCancelReorder={handleCancelReorder}
        onConfirmReorder={handleConfirmReorder}
        refreshStatus={data.refreshStatus}
        lastRefreshedAt={data.lastRefreshedAt}
        refreshLimitReached={refreshUnavailable}
        refreshLimitMessage={refreshUnavailableMessage}
      />

      <GlobalFiltersPanel
        groupId={targetGroupId}
        isRefreshing={isRefreshing}
        refreshLimitReached={refreshUnavailable}
        refreshLimitMessage={refreshUnavailableMessage}
      />

      {!hasPins && (
        <DashboardEmptyState
          onAddSummary={() => openModal("addSummary", { reportGroupId: targetGroupId })}
          isRefreshing={isRefreshing}
        />
      )}

      {hasPins && (
        <div className="dashboard-pins-container">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable 
                droppableId="dashboard-pins" 
                direction={isReorderMode ? "vertical" : "horizontal"}
                isDropDisabled={!isReorderMode || isRefreshing}
            >
              {(provided) => (
                <div className={isReorderMode ? "dashboard-reorder-list" : "row align-items-start"} {...provided.droppableProps} ref={provided.innerRef}>
                  {pinsToRender.map((pin, index) => (
                    <DashboardPinCard
                      key={pin.pinId}
                      pin={pin}
                      index={index}
                      isReorderMode={isReorderMode && !isRefreshing} 
                      isRefreshing={isRefreshing} 
                      isFirst={index === 0}
                      isLast={index === pinsToRender.length - 1}
                      onMoveToTop={() => moveLocalPin(index, 0)}
                      onMoveUp={() => moveLocalPin(index, index - 1)}
                      onMoveDown={() => moveLocalPin(index, index + 1)}
                      onMoveToBottom={() => moveLocalPin(index, pinsToRender.length - 1)}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
    </div>
  );
};

export default ReportDashboardPage;
